import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: Partial<UserModel> | undefined
  setCurrentUser: Dispatch<SetStateAction<Partial<UserModel> | undefined>>
  logout: () => void
  userToken: string | undefined
  setUserToken: Dispatch<SetStateAction<string | undefined>>
  companyId: number | undefined
  storeCompanyId: Dispatch<SetStateAction<number | undefined>>
  investorId: number | undefined
  storeInvestorId: Dispatch<SetStateAction<number | undefined>>
  newCompany: boolean | undefined
  setNewCompany: Dispatch<SetStateAction<boolean | undefined>>
  isShowHelpBanners: boolean | undefined
  setisShowHelpBanners: Dispatch<SetStateAction<boolean>>
  isShowDemoDashboard: boolean | undefined
  setisShowDemoDashboard: Dispatch<SetStateAction<boolean>>
  showBillingModal: boolean
  setShowBillingModal: Dispatch<SetStateAction<boolean>>
  currentState: string
  setCurrentState: Dispatch<SetStateAction<string>>
  selected: string
  setSelected: Dispatch<SetStateAction<string>>
  currencyBill: string
  setCurrencyBill: Dispatch<SetStateAction<string>>
  billingData: any
  setBillingData: Dispatch<SetStateAction<any>>
  setRole: Dispatch<SetStateAction<string | undefined>>
  role: string | undefined
  showBillingAccessModal: boolean
  setShowBillingAccessModal: Dispatch<SetStateAction<boolean>>
  billingPortalUrl: string | undefined
  setBillingPortalUrl: Dispatch<SetStateAction<string | undefined>>
  isFoldersPresent: any
  setIsFoldersPresent: Dispatch<SetStateAction<any>>
  entitlements: any
  setEntitlements: Dispatch<SetStateAction<any>>
  companyCurrency: string
  setCompanyCurrency: Dispatch<SetStateAction<string>>
  companyQueuePosition: number | undefined
  setCompanyQueuePosition: Dispatch<SetStateAction<number | undefined>>
  useCase: string | undefined
  setUseCase: Dispatch<SetStateAction<string | undefined>>
  planStatus: string | undefined
  setPlanStatus: Dispatch<SetStateAction<string | undefined>>
  invoiceStatus: string | undefined
  setInvoiceStatus: Dispatch<SetStateAction<string | undefined>>
  twoFaToken: string | undefined
  setTwoFaToken: Dispatch<SetStateAction<string | undefined>>
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  data: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  userToken: undefined,
  setUserToken: () => {},
  companyId: undefined,
  storeCompanyId: () => {},
  isShowHelpBanners: false,
  setisShowHelpBanners: () => {},
  isShowDemoDashboard: false,
  setisShowDemoDashboard: () => {},
  investorId: undefined,
  storeInvestorId: () => {},
  newCompany: undefined,
  setNewCompany: () => {},
  showBillingModal: false,
  setShowBillingModal: () => {},
  currentState: 'Monthly',
  setCurrentState: () => {},
  selected: 'Basic',
  setSelected: () => {},
  currencyBill: 'USD',
  setCurrencyBill: () => {},
  billingData: undefined,
  setBillingData: () => {},
  role: undefined,
  setRole: () => {},
  showBillingAccessModal: false,
  setShowBillingAccessModal: () => {},
  billingPortalUrl: undefined,
  setBillingPortalUrl: () => {},
  isFoldersPresent: [],
  setIsFoldersPresent: () => {},
  entitlements: undefined,
  setEntitlements: () => {},
  companyCurrency: 'USD',
  setCompanyCurrency: () => {},
  companyQueuePosition: undefined,
  setCompanyQueuePosition: () => {},
  useCase: undefined,
  setUseCase: () => {},
  planStatus: undefined,
  setPlanStatus: () => {},
  invoiceStatus: undefined,
  setInvoiceStatus: () => {},
  twoFaToken: undefined,
  setTwoFaToken: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<Partial<UserModel> | undefined>()
  const [userToken, setUserToken] = useState<string | undefined>()
  const [companyId, storeCompanyId] = useState<number | undefined>()
  const [investorId, storeInvestorId] = useState<number | undefined>()
  const [newCompany, setNewCompany] = useState<boolean | undefined>()
  const [showBillingModal, setShowBillingModal] = useState<boolean>(false)
  const [currentState, setCurrentState] = useState<string>('Monthly')
  const [selected, setSelected] = useState('Basic')
  const [currencyBill, setCurrencyBill] = useState('USD')
  const [companyCurrency, setCompanyCurrency] = useState('USD')
  const [billingData, setBillingData] = useState<any>()
  const [isShowDemoDashboard, setisShowDemoDashboard] = useState(false)
  const [isShowHelpBanners, setisShowHelpBanners] = useState(true)
  const [role, setRole] = useState<any>()
  const [showBillingAccessModal, setShowBillingAccessModal] = useState(false)
  const [billingPortalUrl, setBillingPortalUrl] = useState<string | undefined>()
  const [isFoldersPresent, setIsFoldersPresent] = useState([])
  const [entitlements, setEntitlements] = useState<any[]>([])
  const [companyQueuePosition, setCompanyQueuePosition] = useState<number>()
  const [useCase, setUseCase] = useState<string | undefined>()
  const [planStatus, setPlanStatus] = useState<string | undefined>()
  const [invoiceStatus, setInvoiceStatus] = useState<string | undefined>()
  const [twoFaToken, setTwoFaToken] = useState<string | undefined>()

  const saveAuth = async (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)

      const {data} = await getUserByToken(auth.token)
      if (data) {
        setCurrentUser(data.data)
      }
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = async () => {
    await saveAuth(undefined)
    setCurrentUser(undefined)
    setUserToken(undefined)
    storeCompanyId(undefined)
    setCompanyQueuePosition(undefined)

    const lsLang = localStorage.getItem(I18N_CONFIG_KEY)
    const lsUtmSource = localStorage.getItem('utm_source')
    const lsUtmMedium = localStorage.getItem('utm_medium')
    const lsUtmCampaign = localStorage.getItem('utm_campaign')
    const lsUtmContent = localStorage.getItem('utm_content')
    const lsUtmTerm = localStorage.getItem('utm_term')
    const lsGclid = localStorage.getItem('rk_gclid')

    localStorage.clear()

    if (lsLang) {
      localStorage.setItem(I18N_CONFIG_KEY, lsLang)
    }

    if (lsUtmSource) {
      localStorage.setItem('utm_source', lsUtmSource)
    }

    if (lsUtmMedium) {
      localStorage.setItem('utm_medium', lsUtmMedium)
    }

    if (lsUtmCampaign) {
      localStorage.setItem('utm_campaign', lsUtmCampaign)
    }

    if (lsUtmContent) {
      localStorage.setItem('utm_content', lsUtmContent)
    }

    if (lsUtmTerm) {
      localStorage.setItem('utm_term', lsUtmTerm)
    }

    if (lsGclid) {
      localStorage.setItem('rk_gclid', lsGclid)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        isShowDemoDashboard,
        setisShowDemoDashboard,
        isShowHelpBanners,
        setisShowHelpBanners,
        logout,
        setUserToken,
        userToken,
        companyId,
        storeCompanyId,
        investorId,
        storeInvestorId,
        newCompany,
        setNewCompany,
        showBillingModal,
        setShowBillingModal,
        currentState,
        setCurrentState,
        selected,
        setSelected,
        currencyBill,
        setCurrencyBill,
        billingData,
        setBillingData,
        setRole,
        role,
        showBillingAccessModal,
        setShowBillingAccessModal,
        billingPortalUrl,
        setBillingPortalUrl,
        setIsFoldersPresent,
        isFoldersPresent,
        entitlements,
        setEntitlements,
        companyCurrency,
        setCompanyCurrency,
        companyQueuePosition,
        setCompanyQueuePosition,
        useCase,
        setUseCase,
        planStatus,
        setPlanStatus,
        invoiceStatus,
        setInvoiceStatus,
        twoFaToken, 
        setTwoFaToken
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken: string) => {
      try {
        if (!didRequest.current) {
          const {data} = await getUserByToken(apiToken)
          if (data) {
            setCurrentUser(data.data)
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }
    if (auth && auth.token) {
      requestUser(auth.token)
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
